import React, { useEffect, useState } from "react";
import styles from "../styles/Terms.module.css";
import { EULA_URL } from "../utils/legal";

const Terms = () => {
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (
      window.location.hash === "#privacy-policy" ||
      window.location.pathname.endsWith("/privacy")
    ) {
      const target = document.getElementById("privacy-policy");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.intro}>
        <div className={styles.introText}>
          <h1>RigRestR — Terms of Service & Privacy Policy</h1>
          <p className={styles.subtitle}>RigRestR - Truck Stop & Rest Area Finder</p>
          <p className={styles.lastUpdated}>Last Updated: February 2025</p>

          <p className={styles.termsText}>
            <strong>I AGREE to the EULA.</strong> By using RigRestR, you agree
            to the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
              End User License Agreement (EULA)
            </a>
            , these Terms of Service, and our Privacy Policy.
          </p>

          {/* TERMS OF SERVICE */}
          <h2 id="terms-of-service">Terms of Service</h2>
          
          <h3>Introduction</h3>
          <p>
            Welcome to RigRestR ("we," "our," or "us"). By downloading, accessing, or using our mobile application RigRestR (the "App"), you agree to be bound by these Terms of Service ("Terms").
          </p>
          <p>
            Please read these Terms carefully. If you do not agree with these Terms, please do not use the App.
          </p>

          <h3>Acceptance of Terms</h3>
          <p>
            By creating an account or using RigRestR, you acknowledge that you have read, understood, and agree to be bound by:
            <br />
            • These Terms of Service<br />
            • Our Privacy Policy<br />
            • Apple's / Google's Terms and Conditions (for App Store / Google Play purchases)<br />
            • Our{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">End User License Agreement (EULA)</a>
          </p>
          <p>
            <strong>You agree to the EULA.</strong> By downloading, accessing, subscribing, or using RigRestR in any way, you agree to be bound by the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">End User License Agreement (EULA)</a>{" "}
            linked above, in addition to these Terms of Service and our Privacy Policy.
          </p>

          <h3>About RigRestR</h3>
          <p>
            RigRestR is a mobile application designed specifically for commercial truck drivers, long-haul truckers, and professional drivers. The App helps drivers locate safe, verified rest stops, truck parking areas, and facilities along their routes.
          </p>
          <p>
            <strong>What the App Provides:</strong><br />
            • Display nearby rest stops, truck parking, and facilities based on your location<br />
            • Community-driven location submissions and reports<br />
            • Premium subscription for unlimited access<br />
            • User profiles and referral program
          </p>

          <h3>User Accounts</h3>
          <p>
            <strong>Account Creation:</strong><br />
            • You must provide accurate and complete information<br />
            • One account per person<br />
            • You are responsible for all activity under your account<br />
            • Keep your login credentials secure and confidential
          </p>
          <p>
            <strong>Account Termination:</strong><br />
            • You may delete your account at any time via Profile &gt; Delete Account in the mobile app, or the Account page on the website<br />
            • We may suspend or terminate accounts that violate these Terms<br />
            • Upon termination, your right to use the App ceases immediately
          </p>

          <h3>Acceptable Use Policy</h3>
          <p>
            <strong>You agree to:</strong><br />
            ✓ Provide accurate location information when submitting rest stops<br />
            ✓ Respect other users and the community<br />
            ✓ Use the App for its intended purpose<br />
            ✓ Comply with all applicable laws and regulations
          </p>
          <p>
            <strong>You agree NOT to:</strong><br />
            ✗ Submit false, misleading, or harmful location information<br />
            ✗ Harass, abuse, threaten, or harm other users<br />
            ✗ Attempt to gain unauthorized access to the service<br />
            ✗ Use the App for any illegal purpose<br />
            ✗ Interfere with or disrupt the service<br />
            ✗ Reverse engineer or attempt to extract source code<br />
            ✗ Create multiple accounts or share account credentials
          </p>

          <h3>Subscriptions & Payments</h3>
          <p>
            <strong>Products We Offer:</strong><br />
            RigRestR is free to download and offers the following paid subscription plans sold through the Apple App Store (iOS) and Google Play (Android):
          </p>
          <p>
            <strong>Free Tier:</strong><br />
            • Limited to 200 location views<br />
            • Access to basic features<br />
            • No payment required
          </p>
          <p>
            <strong>RigRestR Pro Monthly Subscription:</strong><br />
            • Price: $20/month (billed monthly)<br />
            • Billing period: 1 month, automatically renewing<br />
            • Unlimited location views for one month<br />
            • All truck stops, rest areas &amp; parking<br />
            • Add &amp; report locations<br />
            • Priority support<br />
            • Includes a free trial period (4-day trial on iOS, 3-day trial on Android) for new subscribers<br />
            • Billed through Apple App Store / Google Play<br />
            • Automatically renews monthly unless cancelled
          </p>
          <p>
            <strong>RigRestR Pro Annual Subscription:</strong><br />
            • Price: $199/year (billed annually)<br />
            • Billing period: 12 months, automatically renewing<br />
            • Unlimited location views for one year<br />
            • All truck stops, rest areas &amp; parking<br />
            • Add &amp; report locations<br />
            • Priority support<br />
            • Best value: saves $41 (about 17%) compared to monthly billing<br />
            • Billed through Apple App Store / Google Play<br />
            • Automatically renews annually unless cancelled<br />
            • Affiliate discount offers may apply (e.g., special pricing with an affiliate code)
          </p>
          <p>
            <strong>Cancellation & Refunds:</strong><br />
            • Cancel anytime via App Store / Google Play &gt; Subscriptions<br />
            • Cancellation takes effect at end of billing period<br />
            • Refunds are handled according to Apple's / Google's refund policies
          </p>
          <p>
            <strong>EULA Agreement:</strong><br />
            By purchasing any subscription product offered by RigRestR, you agree to the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">End User License Agreement (EULA)</a>,{" "}
            these Terms of Service, and our Privacy Policy.
          </p>
          <p>
            <input
              type="checkbox"
              id="eulaAgreement"
              className={styles.termsCheckbox}
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label htmlFor="eulaAgreement" className={styles.termsText}>
              I AGREE to the{" "}
              <a href="#terms-of-service">Terms of Service</a>, the{" "}
              <a href="#privacy-policy">Privacy Policy</a>, and the{" "}
              <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
                End User License Agreement (EULA)
              </a>
              .
            </label>
          </p>

          <h3>Disclaimers</h3>
          <p>
            <strong>THE APP IS PROVIDED "AS IS":</strong><br />
            • We do not guarantee the accuracy, safety, or availability of any listed location<br />
            • Rest stop conditions may change without notice<br />
            • Always use your own judgment before stopping<br />
            • We are not responsible for decisions made based on App information
          </p>

          <h3>Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, RigRestR and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the App.
          </p>

          <h3>Intellectual Property</h3>
          <p>
            The App, including its design, features, and content, is owned by RigRestR. You may not copy, modify, or distribute the App without permission.
          </p>

          <h3>Changes to Terms</h3>
          <p>
            We may update these Terms from time to time. Continued use after changes constitutes acceptance. If you disagree with changes, you may delete your account.
          </p>

          <hr className={styles.divider} />

          {/* PRIVACY POLICY */}
          <h2 id="privacy-policy">Privacy Policy</h2>

          <p className={styles.termsText}>
            <strong>I AGREE to the EULA.</strong> By using RigRestR, you agree
            to the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
              End User License Agreement (EULA)
            </a>
            , this Privacy Policy, and our Terms of Service.
          </p>

          <h3>Introduction</h3>
          <p>
            RigRestR ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application RigRestR (the "App").
          </p>
          <p>
            Please read this Privacy Policy carefully. By using the App, you agree to the collection and use of information in accordance with this policy.
          </p>

          <h3>About RigRestR</h3>
          <p>
            RigRestR is a mobile application designed specifically for commercial truck drivers, long-haul truckers, and professional drivers who spend extended periods on the road. The App helps drivers locate safe, verified rest stops, truck parking areas, and facilities along their routes.
          </p>
          <p>
            <strong>Purpose of the App:</strong><br />
            • Displaying Nearby Rest Stops: Using your device's location, we show you the closest truck stops, rest areas, and parking facilities in real-time<br />
            • Community-Driven Information: Drivers can submit new locations, report issues, and share experiences<br />
            • Safety First: We help drivers find safe places to rest, reducing fatigue-related accidents<br />
            • Unlimited Access for Premium Users: Premium subscribers get unlimited access to all locations and features
          </p>

          <h3>Account Requirement</h3>
          <p>
            Access to RigRestR requires account registration. An account allows you to:<br />
            • Access the nearby rest stop finder<br />
            • Submit new locations to the community<br />
            • Save preferences and settings<br />
            • Access premium features (with active subscription)
          </p>
          <p>
            Certain premium features require an active subscription. Subscriptions can be managed and canceled at any time through your Apple App Store account settings.
          </p>

          <h3>Information We Collect</h3>
          
          <p><strong>Information You Provide:</strong></p>
          <p>
            <strong>Email Address:</strong><br />
            • To create and secure your account<br />
            • To send one-time sign-in verification codes (we never ask for or store a password)<br />
            • To notify you of important account updates<br />
            • To communicate about your subscription status<br />
            • Required: Yes (for account creation)
          </p>
          <p>
            <strong>Username:</strong><br />
            • To identify you within the community<br />
            • To display who submitted or reported locations<br />
            • To personalize your profile<br />
            • Required: Yes (for account creation)
          </p>
          <p>
            <strong>Sign-in Method (Email OTP):</strong><br />
            • RigRestR does not use passwords at all<br />
            • Sign-in codes are sent to your email and expire shortly after being issued<br />
            • No password is ever created, requested, or stored
          </p>

          <p><strong>Information Collected Automatically:</strong></p>
          <p>
            <strong>Location Data (Critical for App Functionality):</strong><br />
            • What We Collect: Your device's precise GPS coordinates<br />
            • Why We Need It: This is the core and essential functionality of RigRestR. The entire purpose of this App is to help truck drivers find nearby rest stops and truck parking. Without your precise GPS location, we have no way to determine which rest areas are closest to you.<br />
            • When We Collect It: ONLY when the App is open and in the foreground<br />
            • Background Tracking: NO. We do NOT track your location when the App is closed or in the background<br />
            • Storage: Location data is processed in real-time and is not stored in our database<br />
            • Your Control: Location access is required to use the nearby rest stop feature, which is the core functionality of the App
          </p>
          <p>
            <strong>Device Information:</strong><br />
            • Device Type/Model: To optimize App performance<br />
            • Operating System Version: To ensure compatibility<br />
            • App Version: For support and bug fixes<br />
            • App-Specific Identifier: A randomly generated app-specific identifier used solely for subscription verification. Not used for advertising or cross-app tracking. We do not collect IDFA.
          </p>
          <p>
            <strong>Usage Data:</strong><br />
            • Features Accessed: To prioritize improvements<br />
            • Crash Reports: To fix bugs and improve stability<br />
            • Performance Metrics: To ensure smooth operation<br />
            • Note: Usage data may be associated with an app-specific identifier for internal analytics purposes only.
          </p>

          <h3>Third-Party Services</h3>
          <p>
            <strong>RevenueCat (Subscription Management):</strong><br />
            • Manages our subscription system through Apple App Store<br />
            • Collects: Purchase history, subscription status, transaction IDs<br />
            • May use device identifiers to prevent subscription fraud and restore purchases across devices<br />
            • Does NOT receive your credit card or payment details<br />
            • Privacy Policy: <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">revenuecat.com/privacy</a>
          </p>
          <p>
            <strong>Supabase (Backend & Authentication):</strong><br />
            • Provides secure backend infrastructure<br />
            • Stores: Email, username, location submissions<br />
            • All data encrypted at rest and in transit<br />
            • SOC 2 Type II compliant<br />
            • Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a>
          </p>
          <p>
            <strong>Apple App Store (Payments):</strong><br />
            • Processes subscription payments<br />
            • We receive confirmation of purchase only<br />
            • We do NOT receive your credit card or billing details<br />
            • Privacy Policy: <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">apple.com/legal/privacy</a>
          </p>

          <h3>What We Do NOT Collect</h3>
          <p>
            ❌ Credit card or payment information<br />
            ❌ Social Security or government IDs<br />
            ❌ Contacts or address book<br />
            ❌ Photos or camera access<br />
            ❌ Microphone or audio<br />
            ❌ Health or fitness data<br />
            ❌ Browsing history<br />
            ❌ Data from other apps<br />
            ❌ Background location tracking<br />
            ❌ Advertising identifiers (IDFA)
          </p>

          <h3>How We Use Your Information</h3>
          <p>
            <strong>Primary Uses:</strong><br />
            • Show nearby rest stops (using location data)<br />
            • Create your account (email, username)<br />
            • Process subscriptions (via RevenueCat)<br />
            • Authenticate you (one-time email codes)<br />
            • Display your submissions (username)
          </p>
          <p>
            <strong>What We Do NOT Use Your Data For:</strong><br />
            ❌ Sell your data to third parties - NEVER<br />
            ❌ Target you with advertisements<br />
            ❌ Track you across other apps/websites<br />
            ❌ Build advertising profiles<br />
            ❌ Share with data brokers<br />
            ❌ Send marketing emails without consent
          </p>

          <h3>Data Retention</h3>
          <p>
            • Account Data: Until account deletion + 30 days<br />
            • Location Data: Not stored permanently; processed in real-time only<br />
            • Subscription Data: Duration of subscription + 1 year<br />
            • Usage Analytics: 24 months<br />
            • Backup Retention: Up to 90 days in encrypted backups
          </p>

          <h3>Your Rights and Choices</h3>
          <p>
            • Access: Request a copy of your personal data<br />
            • Correction: Update or correct inaccurate data<br />
            • Deletion: Request deletion of your account and data<br />
            • Data Portability: Receive your data in a portable format<br />
            • Opt-Out: Contact us for analytics opt-out<br />
            • Withdraw Consent: At any time
          </p>
          <p>
            <strong>How to Exercise Your Rights:</strong><br />
            • Delete Account: Go to Profile &gt; Delete Account in the mobile app, or the Account page on the website<br />
            • Contact Us: Rigrestrapp@gmail.com
          </p>

          <h3>Data Security</h3>
          <p>
            • Encryption: Data encrypted in transit (TLS/SSL) and at rest<br />
            • Authentication: One-time email codes (no passwords are ever stored)<br />
            • Access Control: Role-based access to user data<br />
            • Infrastructure: Hosted on secure, compliant cloud providers
          </p>

          <h3>California Privacy Rights (CCPA)</h3>
          <p>
            If you are a California resident, you have additional rights:<br />
            • Right to Know what personal information we collect<br />
            • Right to Delete your personal information<br />
            • Right to Opt-Out of the sale of personal information (we do not sell data)<br />
            • Non-Discrimination for exercising your rights
          </p>

          <h3>European Privacy Rights (GDPR)</h3>
          <p>
            If you are in the European Economic Area (EEA), you have rights under GDPR:<br />
            • Access, rectification, and erasure of your data<br />
            • Data portability<br />
            • Object to or restrict processing<br />
            • Lodge a complaint with a supervisory authority
          </p>
          <p>
            <strong>Data Controller:</strong> RigRestR, operated by Saad Ahmed, located in Pakistan.<br />
            <strong>Contact:</strong> Rigrestrapp@gmail.com
          </p>
          <p>
            <strong>Legal Basis for Processing (GDPR Article 6):</strong><br />
            • Account Data: Contractual Necessity<br />
            • Location Data: Contractual Necessity (required for core functionality)<br />
            • Subscription Data: Contractual Necessity<br />
            • Usage Analytics: Legitimate Interest<br />
            • Crash Reports: Legitimate Interest
          </p>

          <h3>Apple App Store Compliance</h3>
          <p>
            • We do NOT track users across apps or websites<br />
            • We do NOT use IDFA (Identifier for Advertisers)<br />
            • We do NOT serve targeted ads<br />
            • We do NOT share data with ad networks<br />
            • We follow Apple's principle of data minimization
          </p>

          <h3>Summary of Data Practices</h3>
          <p>
            <strong>We do not:</strong><br />
            ❌ Sell your data to third parties<br />
            ❌ Track you across other apps or websites<br />
            ❌ Collect location when the app is in the background<br />
            ❌ Share data with advertising networks
          </p>
          <p>
            <strong>We do:</strong><br />
            ✅ Encrypt your data in transit and at rest<br />
            ✅ Allow you to delete your account and data<br />
            ✅ Respect your privacy choices and permissions<br />
            ✅ Comply with GDPR, CCPA, and Apple App Store guidelines
          </p>

          <hr className={styles.divider} />

          <h3>Contact Us</h3>
          <p>
            For questions about these Terms or Privacy Policy:<br />
            <strong>Email:</strong> Rigrestrapp@gmail.com
          </p>

          <p className={styles.termsText}>
            <strong>I AGREE to the EULA.</strong> By continuing to use
            RigRestR, you agree to the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
              End User License Agreement (EULA)
            </a>
            , these Terms of Service, and our Privacy Policy.
          </p>

          <p className={styles.effectiveDate}>
            <em>This document is effective as of February 2025 and was last updated February 2025.</em>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
